import React, { useState } from 'react';
import { getRoleBadge } from '../utils/roles';
import { useSelector } from 'react-redux';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Button, 
  Form, 
  Badge,
  Modal,
  Alert
} from 'react-bootstrap';
import { format } from 'date-fns';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { RootState } from '../store';
import { User } from '../api/generated/model';

const ProfilePage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState<User | null>(user);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  if (!user) {
    return (
      <Container className="py-5">
        <Alert variant="warning">
          <i className="bi bi-exclamation-triangle me-2"></i>
          Unable to load user profile. Please try refreshing the page.
        </Alert>
      </Container>
    );
  }


  const handleSaveProfile = async () => {
    if (!editedUser) return;

    try {
      // Here you would typically make an API call to update the user profile
      console.log('Saving profile:', editedUser);
      
      // For now, just simulate success
      setIsEditing(false);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (error) {
      console.error('Failed to save profile:', error);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert('New passwords do not match');
      return;
    }

    try {
      // Here you would make an API call to change the password
      console.log('Changing password...');
      
      setShowChangePassword(false);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (error) {
      console.error('Failed to change password:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMMM d, yyyy');
  };

  return (
    <DashboardLayout
      title="My Profile"
      subtitle="Manage your account settings and preferences"
      scrollable
    >
      <Container fluid className="py-4">
        {showSuccessMessage && (
          <Alert variant="success" dismissible onClose={() => setShowSuccessMessage(false)}>
            <i className="bi bi-check-circle me-2"></i>
            Profile updated successfully!
          </Alert>
        )}

      <Row>
        <Col lg={4} className="mb-4">
          <Card className="">
            <Card.Body className="text-center">
              <div className="mb-4">
                <i className="bi bi-person-circle text-primary" style={{ fontSize: '5rem' }}></i>
              </div>
              <h4 className="mb-1">{user.first_name} {user.last_name}</h4>
              <p className="text-muted mb-3">{user.email}</p>
              <div className="mb-3">
                {getRoleBadge(user.role || '')}
              </div>
              {user.territory && (
                <p className="text-muted mb-3">
                  <i className="bi bi-geo-alt me-1"></i>
                  {user.territory}
                </p>
              )}
              <div className="mb-3">
                <Badge bg={user.active ? 'success' : 'secondary'}>
                  {user.active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <p className="text-muted small mb-0">
                Member since {formatDate(user.created_at || '')}
              </p>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={8}>
          <Card className="mb-4">
            <Card.Header className="">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Profile Information</h5>
                {!isEditing ? (
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={() => {
                      setIsEditing(true);
                      setEditedUser(user);
                    }}
                  >
                    <i className="bi bi-pencil me-1"></i>
                    Edit Profile
                  </Button>
                ) : (
                  <div className="d-flex gap-2">
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={() => {
                        setIsEditing(false);
                        setEditedUser(user);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      variant="primary" 
                      size="sm"
                      onClick={handleSaveProfile}
                    >
                      <i className="bi bi-check me-1"></i>
                      Save Changes
                    </Button>
                  </div>
                )}
              </div>
            </Card.Header>
            <Card.Body>
              {!isEditing ? (
                <Row>
                  <Col md={6}>
                    <dl className="row">
                      <dt className="col-sm-4">First Name:</dt>
                      <dd className="col-sm-8">{user.first_name}</dd>
                      
                      <dt className="col-sm-4">Last Name:</dt>
                      <dd className="col-sm-8">{user.last_name}</dd>
                      
                      <dt className="col-sm-4">Email:</dt>
                      <dd className="col-sm-8">{user.email}</dd>
                    </dl>
                  </Col>
                  <Col md={6}>
                    <dl className="row">
                      <dt className="col-sm-4">Role:</dt>
                      <dd className="col-sm-8">{getRoleBadge(user.role || '')}</dd>
                      
                      <dt className="col-sm-4">Territory:</dt>
                      <dd className="col-sm-8">{user.territory || 'Not assigned'}</dd>
                      
                      <dt className="col-sm-4">Status:</dt>
                      <dd className="col-sm-8">
                        <Badge bg={user.active ? 'success' : 'secondary'}>
                          {user.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </dd>
                    </dl>
                  </Col>
                </Row>
              ) : (
                <Form>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>First Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={editedUser?.first_name || ''}
                          onChange={(e) => setEditedUser(prev => prev ? 
                            { ...prev, first_name: e.target.value } : null
                          )}
                        />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Last Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={editedUser?.last_name || ''}
                          onChange={(e) => setEditedUser(prev => prev ? 
                            { ...prev, last_name: e.target.value } : null
                          )}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          value={editedUser?.email || ''}
                          onChange={(e) => setEditedUser(prev => prev ? 
                            { ...prev, email: e.target.value } : null
                          )}
                        />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Territory</Form.Label>
                        <Form.Control
                          type="text"
                          value={editedUser?.territory || ''}
                          onChange={(e) => setEditedUser(prev => prev ? 
                            { ...prev, territory: e.target.value } : null
                          )}
                          placeholder="Enter territory"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Form>
              )}
            </Card.Body>
          </Card>

          <Card className="">
            <Card.Header className="">
              <h5 className="mb-0">Security Settings</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h6 className="mb-1">Password</h6>
                      <p className="text-muted small mb-0">
                        Last updated {formatDate(user.updated_at || '')}
                      </p>
                    </div>
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={() => setShowChangePassword(true)}
                    >
                      <i className="bi bi-key me-1"></i>
                      Change Password
                    </Button>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <h6 className="mb-1">Account Status</h6>
                    <Badge bg={user.active ? 'success' : 'secondary'} className="fs-6">
                      {user.active ? 'Active Account' : 'Inactive Account'}
                    </Badge>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Change Password Modal */}
      <Modal show={showChangePassword} onHide={() => setShowChangePassword(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Change Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Current Password</Form.Label>
              <Form.Control
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm(prev => 
                  ({ ...prev, currentPassword: e.target.value })
                )}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm(prev => 
                  ({ ...prev, newPassword: e.target.value })
                )}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Confirm New Password</Form.Label>
              <Form.Control
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm(prev => 
                  ({ ...prev, confirmPassword: e.target.value })
                )}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowChangePassword(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleChangePassword}>
            <i className="bi bi-check me-1"></i>
            Change Password
          </Button>
        </Modal.Footer>
      </Modal>
      </Container>
    </DashboardLayout>
  );
};

export default ProfilePage;